import {
  Button,
  Card,
  ColorPicker,
  ConfigProvider,
  Flex,
  Space,
  Typography,
} from "antd";
import { BrandKit } from "../../types";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme";
import { CSSProperties } from "react";
import { CanAccess } from "@refinedev/core";
import { Check, Pencil, Trash } from "@phosphor-icons/react";
import { DeleteButton } from "@refinedev/antd";
import { radius } from "@libertify/ui";

export const FlatBrandKitPreview = ({
  brandKit,
  organizationId,
  width,
  height = 350,
  style,
  onEdit,
  handleClick,
  deleteButton = true,
  checked,
}: {
  brandKit: BrandKit;
  organizationId: string;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
  onEdit: () => void;
  handleClick?: () => void;
  deleteButton?: boolean;
  checked?: boolean;
}) => {
  const titleFont = brandKit?.assets.find((asset) => asset.purpose === "Title");
  const subtitlesFont = brandKit?.assets?.find(
    (asset) => asset.purpose === "Subtitles"
  );
  const { theme } = useAntTheme();
  const logoPath = brandKit?.assets?.find(
    (asset) => asset.purpose === "Logo"
  )?.path;

  const fontFace = `
    @font-face {
      font-family: 'PreviewFont-${brandKit.id}';
      src: url('${titleFont?.path ?? subtitlesFont?.path}') format('opentype'), url('${titleFont?.path ?? subtitlesFont?.path}') format('truetype');
      font-display: swap;
    }`;

  return (
    <>
      <style type="text/css">{fontFace}</style>
      <Card
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (
            target.closest(".ant-radio-button") ||
            target.closest(".ant-radio-group") ||
            target.closest(".ant-btn")
          ) {
            return;
          }
          handleClick?.();
        }}
        hoverable
        style={{
          flex: 1,
          backgroundColor: theme.colorBgBase,
          borderRadius: radius.box,
          boxShadow: `0 0 10px ${theme.colorBorder}`,
          height,
          width,
          ...style,
          padding: 0,
        }}
        styles={{
          body: {
            height: "100%",
            width: "100%",
            padding: 20,
            position: "relative",
          },
        }}
      >
        {checked && (
          <Flex
            align="center"
            justify="center"
            style={{
              position: "absolute",
              backgroundColor: theme.colorPrimary,
              width: 40,
              height: 40,
              border: `5px solid ${theme.colorBgElevated}`,
              borderRadius: radius.circle,
              color: theme.colorBgElevated,
              right: -10,
              top: -10,
              zIndex: 2,
            }}
          >
            <Check size={22} />
          </Flex>
        )}
        <Flex justify="end">
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  defaultHoverBg: "rgba(0,0,0,0.04) !important",
                },
              },
            }}
          >
            <Space>
              <CanAccess resource="media_brandkits" action="edit">
                <Button
                  icon={
                    <Pencil
                      size={23}
                      style={{
                        color: theme.colorTextBase,
                      }}
                    />
                  }
                  size="large"
                  type="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                />
              </CanAccess>
              {deleteButton && (
                <CanAccess resource="media_brandkits" action="delete">
                  <DeleteButton
                    size="large"
                    icon={<Trash size={23} />}
                    hideText
                    resource={`media/${organizationId}/brand_kits`}
                    recordItemId={brandKit.id}
                    accessControl={{ enabled: false }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{
                      color: theme.colorTextBase,
                      border: "none",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  />
                </CanAccess>
              )}
            </Space>
          </ConfigProvider>
        </Flex>
        <Flex
          vertical
          align="center"
          justify="start"
          style={{ height: "100%" }}
        >
          <div
            style={{
              height: "40%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {logoPath && (
              <img
                src={logoPath}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
                alt={`logo-${brandKit?.name}`}
              />
            )}
          </div>
          <Flex
            vertical
            align="center"
            justify="center"
            style={{ position: "absolute", top: "65%" }}
            gap={10}
          >
            <Space>
              <ColorPreview
                color={brandKit.palette.primary_color}
                borderColor={theme.colorTextBase}
              />
              <ColorPreview
                color={brandKit.palette.secondary_color}
                borderColor={theme.colorTextBase}
              />
            </Space>
            <Typography.Paragraph
              ellipsis={{ rows: 2 }}
              style={{
                fontSize: 20,
                color: theme.colorTextBase,
                lineHeight: 1.3,
                textAlign: "center",
                margin: 10,
                marginTop: 0,
              }}
            >
              {brandKit?.name}
            </Typography.Paragraph>
          </Flex>
        </Flex>
      </Card>
    </>
  );
};

export const ColorPreview = ({
  color,
  borderColor,
  size = 37,
}: {
  color: string;
  borderColor: string;
  size?: number;
}) => {
  return (
    <StyledColorPicker
      value={color}
      defaultValue={color}
      open={false}
      $borderColor={borderColor}
      $size={size}
    />
  );
};

interface ColorPickerProps {
  $borderColor: string;
  $size: number;
}

const StyledColorPicker = styled(ColorPicker)<ColorPickerProps>`
  padding: 0 !important;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  display: block;
  border: 1px solid ${(props) => props.$borderColor};
  pointer-events: none;
  cursor: default;

  &:hover {
    border-color: white;
  }

  .ant-color-picker-color-block {
    width: 100%;
    height: 100%;

    .ant-color-picker-color-block-inner {
      width: 100%;
      height: 100%;
    }
  }

  .ant-color-picker-clear {
    width: 100%;
    height: 100%;
  }

  .ant-color-picker-clear::after {
    width: 45px;
  }
`;
